// src/pages/admin/AdminKyc.tsx
import React, { useState, useEffect } from "react";
import { GlobalStyles } from "../../styles/globalStyles";
import {
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  User,
  FileText,
  Shield,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
  Image as ImageIcon,
  File,
  Loader2,
  Sparkles,
  Filter,
  ChevronDown,
  TrendingUp,
  UserCheck,
  AlertTriangle,
} from "lucide-react";
import toast from "react-hot-toast";
import { kycService } from "../../services/api";

interface KycSubmission {
  id: number;
  user_id: number;
  document_type: string;
  status: string;
  admin_notes: string;
  submitted_at: string;
  verified_at?: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    username: string;
    phone?: string;
    verification_level: number;
  };
  document_front_url?: string;
  document_back_url?: string;
  selfie_photo_url?: string;
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

// Modal Component
const KycDetailsModal: React.FC<{
  submission: KycSubmission | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ submission, isOpen, onClose }) => {
  if (!isOpen || !submission) return null;

  const userName = submission.user
    ? `${submission.user.first_name} ${submission.user.last_name}`
    : `User #${submission.user_id}`;
  const userEmail = submission.user?.email || "N/A";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200/30 dark:border-gray-700/30">
            <div>
              <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                KYC Submission Details
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                ID: {submission.id} • Submitted:{" "}
                {new Date(submission.created_at).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors backdrop-blur-sm group/close"
            >
              <X className="h-5 w-5 group-hover/close:scale-110 transition-transform" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[70vh]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* User Information */}
              <div className="space-y-4">
                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-4 backdrop-blur-sm">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    User Information
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center group">
                      <div className="p-2 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 mr-3 group-hover:scale-110 transition-transform">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {userName}
                      </span>
                    </div>
                    <div className="flex items-center text-sm">
                      <span className="text-gray-500 dark:text-gray-400 min-w-12 mr-3">
                        Email:
                      </span>
                      <span className="text-gray-900 dark:text-white">
                        {userEmail}
                      </span>
                    </div>
                    {submission.user?.phone && (
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 dark:text-gray-400 min-w-12 mr-3">
                          Phone:
                        </span>
                        <span className="text-gray-900 dark:text-white">
                          {submission.user.phone}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center text-sm">
                      <span className="text-gray-500 dark:text-gray-400 min-w-12 mr-3">
                        Verification:
                      </span>
                      <span
                        className={`${GlobalStyles.badge} ${GlobalStyles.badgePrimary}`}
                      >
                        Level {submission.user?.verification_level || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Document Information */}
                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-4 backdrop-blur-sm">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center">
                    <FileText className="h-4 w-4 mr-2" />
                    Document Information
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center group">
                      <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-400/5 mr-3 group-hover:scale-110 transition-transform">
                        <File className="h-4 w-4 text-blue-500" />
                      </div>
                      <span className={`${GlobalStyles.badge}`}>
                        {submission.document_type
                          .replace("_", " ")
                          .toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center text-sm group">
                      <Clock className="h-4 w-4 text-gray-400 mr-3 transition-transform group-hover:scale-110" />
                      <span className="text-gray-500 dark:text-gray-400">
                        Submitted:{" "}
                        <span className="text-gray-900 dark:text-white">
                          {new Date(submission.created_at).toLocaleString()}
                        </span>
                      </span>
                    </div>
                    {submission.verified_at && (
                      <div className="flex items-center text-sm group">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-3 transition-transform group-hover:scale-110" />
                        <span className="text-gray-500 dark:text-gray-400">
                          Verified:{" "}
                          <span className="text-gray-900 dark:text-white">
                            {new Date(submission.verified_at).toLocaleString()}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-4 backdrop-blur-sm">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center">
                    <Shield className="h-4 w-4 mr-2" />
                    Status
                  </h4>
                  <div className="flex items-center group">
                    {getStatusIcon(submission.status)}
                    <span
                      className={`${GlobalStyles.badge} ml-3 ${getStatusBadge(
                        submission.status,
                      )}`}
                    >
                      {submission.status.charAt(0).toUpperCase() +
                        submission.status.slice(1)}
                    </span>
                  </div>
                </div>

                {/* Admin Notes */}
                {submission.admin_notes && (
                  <div className="bg-gradient-to-br from-yellow-500/5 to-orange-500/5 border border-yellow-200/50 dark:border-yellow-800/50 rounded-xl p-4 backdrop-blur-sm">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Admin Notes
                    </h4>
                    <div className="p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {submission.admin_notes}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Document Images */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Document Images
                </h4>

                {/* Front Document */}
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Document Front
                  </p>
                  {submission.document_front_url ? (
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      <img
                        src={submission.document_front_url}
                        alt="Document Front"
                        className="w-full h-48 object-cover rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm group-hover:shadow-md transition-all"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://via.placeholder.com/400x300?text=Image+Not+Available";
                        }}
                      />
                      <a
                        href={submission.document_front_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <span className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium shadow-lg transition-transform hover:scale-105">
                          View Full Size
                        </span>
                      </a>
                    </div>
                  ) : (
                    <div className="w-full h-48 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-800/50 dark:to-gray-900/30 rounded-xl border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm">
                      <div className="text-center">
                        <div className="mx-auto w-12 h-12 bg-gray-200/50 dark:bg-gray-700/50 rounded-full flex items-center justify-center mb-3">
                          <ImageIcon className="h-6 w-6 text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-500">
                          Image not available
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Back Document (if exists) */}
                {submission.document_back_url && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      Document Back
                    </p>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      <img
                        src={submission.document_back_url}
                        alt="Document Back"
                        className="w-full h-48 object-cover rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm group-hover:shadow-md transition-all"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://via.placeholder.com/400x300?text=Image+Not+Available";
                        }}
                      />
                      <a
                        href={submission.document_back_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <span className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium shadow-lg transition-transform hover:scale-105">
                          View Full Size
                        </span>
                      </a>
                    </div>
                  </div>
                )}

                {/* Selfie Photo */}
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Selfie Photo
                  </p>
                  {submission.selfie_photo_url ? (
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      <img
                        src={submission.selfie_photo_url}
                        alt="Selfie"
                        className="w-full h-48 object-cover rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm group-hover:shadow-md transition-all"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://via.placeholder.com/400x300?text=Image+Not+Available";
                        }}
                      />
                      <a
                        href={submission.selfie_photo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <span className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium shadow-lg transition-transform hover:scale-105">
                          View Full Size
                        </span>
                      </a>
                    </div>
                  ) : (
                    <div className="w-full h-48 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-800/50 dark:to-gray-900/30 rounded-xl border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm">
                      <div className="text-center">
                        <div className="mx-auto w-12 h-12 bg-gray-200/50 dark:bg-gray-700/50 rounded-full flex items-center justify-center mb-3">
                          <User className="h-6 w-6 text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-500">
                          Image not available
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end p-6 border-t border-gray-200/30 dark:border-gray-700/30 backdrop-blur-sm">
            <button
              onClick={onClose}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} backdrop-blur-sm group/close-btn`}
            >
              <span>Close</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Status Update Modal
const StatusUpdateModal: React.FC<{
  submission: KycSubmission | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (status: string, notes: string) => Promise<void>;
  isLoading: boolean;
}> = ({ submission, isOpen, onClose, onConfirm, isLoading }) => {
  const [status, setStatus] = useState<string>("approved");
  const [notes, setNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !submission) return null;

  const userName = submission.user
    ? `${submission.user.first_name} ${submission.user.last_name}`
    : `User #${submission.user_id}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onConfirm(status, notes);
      setNotes("");
      onClose();
    } catch (error) {
      // Error is handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200/30 dark:border-gray-700/30">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Update KYC Status
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors backdrop-blur-sm disabled:opacity-50 group/close"
            >
              <X className="h-5 w-5 group-hover/close:scale-110 transition-transform" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Updating status for:{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  {userName}
                </span>
              </p>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Status
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center group/status">
                  <input
                    type="radio"
                    name="status"
                    value="approved"
                    checked={status === "approved"}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center text-green-600 dark:text-green-400 group-hover/status:text-green-500 transition-colors">
                    <CheckCircle className="h-4 w-4 mr-1 transition-transform group-hover/status:-translate-y-0.5" />
                    Approve
                  </span>
                </label>
                <label className="flex items-center group/status">
                  <input
                    type="radio"
                    name="status"
                    value="rejected"
                    checked={status === "rejected"}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-red-500 focus:ring-red-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center text-red-600 dark:text-red-400 group-hover/status:text-red-500 transition-colors">
                    <XCircle className="h-4 w-4 mr-1 transition-transform group-hover/status:-translate-y-0.5" />
                    Reject
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Admin Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this KYC decision..."
                rows={3}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.input} w-full backdrop-blur-sm`}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                These notes will be visible to the user.
              </p>
            </div>

            {/* Warning for rejection */}
            {status === "rejected" && (
              <div className="p-4 bg-gradient-to-br from-yellow-500/5 to-orange-500/5 border border-yellow-200/50 dark:border-yellow-800/50 rounded-xl backdrop-blur-sm">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    Rejecting will reset the user's verification level to 1.
                    They will need to resubmit KYC.
                  </p>
                </div>
              </div>
            )}

            {/* Success for approval */}
            {status === "approved" && (
              <div className="p-4 bg-gradient-to-br from-green-500/5 to-emerald-500/5 border border-green-200/50 dark:border-green-800/50 rounded-xl backdrop-blur-sm">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Approving will set the user's verification level to 3,
                    allowing full platform access.
                  </p>
                </div>
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end p-6 border-t border-gray-200/30 dark:border-gray-700/30 space-x-3 backdrop-blur-sm">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} backdrop-blur-sm disabled:opacity-50`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${
                status === "approved"
                  ? GlobalStyles.buttonSuccess
                  : GlobalStyles.buttonError
              } ${GlobalStyles.buttonMedium} min-w-24 backdrop-blur-sm disabled:opacity-50 group/submit`}
            >
              {isSubmitting || isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isSubmitting ? "Updating..." : "Loading..."}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  {status === "approved" ? (
                    <CheckCircle className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-x-0.5" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-x-0.5" />
                  )}
                  <span>{status === "approved" ? "Approve" : "Reject"}</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getStatusIcon = (status: string) => {
  switch (status) {
    case "approved":
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case "rejected":
      return <XCircle className="h-4 w-4 text-red-500" />;
    case "pending":
      return <Clock className="h-4 w-4 text-yellow-500 animate-pulse" />;
    default:
      return <AlertCircle className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "approved":
      return GlobalStyles.badgeSuccess;
    case "rejected":
      return GlobalStyles.badgeError;
    case "pending":
      return GlobalStyles.badgeWarning;
    default:
      return "";
  }
};

const AdminKyc: React.FC = () => {
  const [kycSubmissions, setKycSubmissions] = useState<KycSubmission[]>([]);
  const [filter, setFilter] = useState({
    status: "",
    document_type: "",
    search: "",
  });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });

  // Modal states
  const [selectedSubmission, setSelectedSubmission] =
    useState<KycSubmission | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string>("");

  useEffect(() => {
    fetchKycSubmissions();
  }, [filter.status, filter.document_type, pagination.current_page]);

  const fetchKycSubmissions = async () => {
    setLoading(true);
    try {
      const params: any = {
        status: filter.status || undefined,
        document_type: filter.document_type || undefined,
        page: pagination.current_page,
      };

      const response = await kycService.getAllKyc(params);

      // Handle the paginated response correctly
      if (response.data && Array.isArray(response.data)) {
        // If response has data array (Laravel pagination)
        setKycSubmissions(response.data);

        if (response.meta) {
          setPagination({
            current_page: response.meta.current_page || 1,
            last_page: response.meta.last_page || 1,
            per_page: response.meta.per_page || 20,
            total: response.meta.total || 0,
          });
        }
      } else if (Array.isArray(response)) {
        // If response is directly an array
        setKycSubmissions(response);
        setPagination({
          current_page: 1,
          last_page: 1,
          per_page: response.length,
          total: response.length,
        });
      } else {
        // Fallback
        const data =
          response.data || response.items || response.submissions || [];
        setKycSubmissions(Array.isArray(data) ? data : []);

        if (response.meta || response.pagination) {
          const meta = response.meta || response.pagination || {};
          setPagination({
            current_page: meta.current_page || 1,
            last_page: meta.last_page || 1,
            per_page: meta.per_page || 20,
            total: meta.total || data.length || 0,
          });
        }
      }
    } catch (error: any) {
      console.error("Failed to fetch KYC submissions:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to load KYC submissions";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (submission: KycSubmission) => {
    fetchKycDetails(submission.id);
  };

  const fetchKycDetails = async (id: number) => {
    try {
      setUpdatingId(id);
      const response = await kycService.getKycDetails(id);
      setSelectedSubmission(response);
      setDetailsModalOpen(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to load KYC details");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStatusUpdateClick = (submission: KycSubmission) => {
    setSelectedSubmission(submission);
    setStatusModalOpen(true);
  };

  const handleStatusUpdate = async (status: string, notes: string) => {
    if (!selectedSubmission) return;

    try {
      setUpdatingStatus(status);
      await kycService.updateKycStatus(selectedSubmission.id, {
        status,
        admin_notes: notes || undefined,
      });

      // Update local state
      setKycSubmissions((prev) =>
        prev.map((submission) =>
          submission.id === selectedSubmission.id
            ? {
                ...submission,
                status,
                admin_notes: notes || submission.admin_notes,
                verified_at:
                  status === "approved"
                    ? new Date().toISOString()
                    : submission.verified_at,
                user: submission.user
                  ? {
                      ...submission.user,
                      verification_level: status === "approved" ? 3 : 1,
                    }
                  : submission.user,
              }
            : submission,
        ),
      );

      toast.success(`KYC ${status} successfully`);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Failed to update KYC status";
      toast.error(errorMessage);
      throw error;
    } finally {
      setUpdatingStatus("");
      setSelectedSubmission(null);
    }
  };

  const handleRefresh = () => {
    fetchKycSubmissions();
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.last_page) {
      setPagination((prev) => ({ ...prev, current_page: page }));
    }
  };

  const handleClearFilters = () => {
    setFilter({
      status: "",
      document_type: "",
      search: "",
    });
    setPagination({
      current_page: 1,
      last_page: pagination.last_page,
      per_page: pagination.per_page,
      total: pagination.total,
    });
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      pagination.current_page - Math.floor(maxVisiblePages / 2),
    );
    let endPage = Math.min(
      pagination.last_page,
      startPage + maxVisiblePages - 1,
    );

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-sm ${
            pagination.current_page === i
              ? "bg-gradient-to-r from-primary to-primary/90 text-white shadow-sm"
              : "bg-white/50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 border border-gray-200/50 dark:border-gray-700/50"
          }`}
        >
          {i}
        </button>,
      );
    }

    return pages;
  };

  const filteredSubmissions = kycSubmissions.filter((submission) => {
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      const userName = submission.user
        ? `${submission.user.first_name || ""} ${
            submission.user.last_name || ""
          }`.toLowerCase()
        : "";
      const userEmail = submission.user?.email?.toLowerCase() || "";
      const userId = submission.user_id.toString();

      return (
        userName.includes(searchLower) ||
        userEmail.includes(searchLower) ||
        userId.includes(searchLower) ||
        submission.document_type.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Calculate statistics
  const pendingCount = kycSubmissions.filter(
    (s) => s.status === "pending",
  ).length;
  const approvedCount = kycSubmissions.filter(
    (s) => s.status === "approved",
  ).length;
  const rejectedCount = kycSubmissions.filter(
    (s) => s.status === "rejected",
  ).length;
  const todayCount = kycSubmissions.filter(
    (s) => new Date(s.created_at).toDateString() === new Date().toDateString(),
  ).length;

  // Calculate percentages safely
  const totalCount = kycSubmissions.length; // Avoid division by zero
  const pendingPercentage = (pendingCount / totalCount) * 100;
  const approvedPercentage = (approvedCount / totalCount) * 100;
  const rejectedPercentage = (rejectedCount / totalCount) * 100;
  const successRate =
    approvedCount > 0 ? (Number(approvedCount) / Number(totalCount)) * 100 : 0;


  if (loading && kycSubmissions.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 p-12">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto"></div>
                <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
              </div>
              <p className="mt-6 text-gray-600 dark:text-gray-400 font-medium">
                Loading KYC submissions...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      <div className="p-4 lg:p-5">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              KYC Management
            </h1>
            <Sparkles className="h-4 w-4 text-primary/60" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            Review and manage KYC submissions
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100/50 to-blue-50/50 dark:from-blue-900/20 dark:to-blue-800/10">
                <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgePrimary} px-2 py-0.5`}
              >
                Total
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Submissions
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {pagination.total}
            </p>
            <div className="flex items-center mt-4 pt-3 border-t border-gray-100/50 dark:border-gray-700/50">
              <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
              <span className="text-xs text-green-600 dark:text-green-400">
                {todayCount} new today
              </span>
            </div>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-yellow-100/50 to-yellow-50/50 dark:from-yellow-900/20 dark:to-yellow-800/10">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeWarning} px-2 py-0.5`}
              >
                Pending
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Pending Review
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {pendingCount}
            </p>
            <div className="mt-4">
              <div className="h-2 bg-gray-200/50 dark:bg-gray-700/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500"
                  style={{
                    width: `${pendingPercentage}%`,
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {pendingPercentage.toFixed(1)}% of total
              </p>
            </div>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-green-100/50 to-green-50/50 dark:from-green-900/20 dark:to-green-800/10">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess} px-2 py-0.5`}
              >
                Approved
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Approved Submissions
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {approvedCount}
            </p>
            <div className="flex items-center mt-4 pt-3 border-t border-gray-100/50 dark:border-gray-700/50">
              <UserCheck className="h-3 w-3 text-green-500 mr-1" />
              <span className="text-xs text-green-600 dark:text-green-400">
                {successRate.toFixed(1)}% success rate
              </span>
            </div>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-red-100/50 to-red-50/50 dark:from-red-900/20 dark:to-red-800/10">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeError} px-2 py-0.5`}
              >
                Rejected
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Rejected Submissions
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {rejectedCount}
            </p>
            <div className="mt-4">
              <div className="h-2 bg-gray-200/50 dark:bg-gray-700/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-red-400 to-red-500"
                  style={{
                    width: `${rejectedPercentage}%`,
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {rejectedPercentage.toFixed(1)}% of total
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Filters
              </h3>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleClearFilters}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} backdrop-blur-sm group/clear`}
              >
                <span>Clear Filters</span>
              </button>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} backdrop-blur-sm group/refresh disabled:opacity-50`}
              >
                {loading ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-2 transition-transform group-hover/refresh:rotate-180" />
                )}
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              <input
                type="text"
                placeholder="Search by name, email, or document type..."
                value={filter.search}
                onChange={(e) =>
                  setFilter({ ...filter, search: e.target.value })
                }
                className={`${GlobalStyles.input} pl-10 backdrop-blur-sm`}
              />
            </div>

            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <Shield className="h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              </div>
              <select
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
                className={`${GlobalStyles.input} pl-10 appearance-none pr-8 backdrop-blur-sm`}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>

            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <File className="h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              </div>
              <select
                value={filter.document_type}
                onChange={(e) =>
                  setFilter({ ...filter, document_type: e.target.value })
                }
                className={`${GlobalStyles.input} pl-10 appearance-none pr-8 backdrop-blur-sm`}
              >
                <option value="">All Documents</option>
                <option value="passport">Passport</option>
                <option value="id_card">ID Card</option>
                <option value="driver_license">Driver License</option>
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200/30 dark:border-gray-700/30 mt-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {filteredSubmissions.length}
                </span>{" "}
                submissions match your filters
              </p>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Page {pagination.current_page} of {pagination.last_page}
            </div>
          </div>
        </div>

        {/* KYC Table */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-900/50 backdrop-blur-sm">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Document Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Verified
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Admin Notes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200/30 dark:divide-gray-700/30">
                {filteredSubmissions.length > 0 ? (
                  filteredSubmissions.map((submission) => {
                    const userName = submission.user
                      ? `${submission.user.first_name || ""} ${
                          submission.user.last_name || ""
                        }`.trim() || `User #${submission.user_id}`
                      : `User #${submission.user_id}`;
                    const userEmail = submission.user?.email || "N/A";

                    return (
                      <tr
                        key={submission.id}
                        className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-all duration-200 group/row"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mr-3 group-hover/row:scale-110 transition-transform">
                              <User className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {userName}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {userEmail}
                              </p>
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                ID: {submission.user_id}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`${GlobalStyles.badge} ${GlobalStyles.badgeInfo}`}
                          >
                            {submission.document_type
                              .replace("_", " ")
                              .toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(submission.status)}
                            <span
                              className={`${GlobalStyles.badge} ml-3 ${getStatusBadge(submission.status)}`}
                            >
                              {submission.status.charAt(0).toUpperCase() +
                                submission.status.slice(1)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {new Date(
                                submission.created_at,
                              ).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(
                                submission.created_at,
                              ).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {submission.verified_at
                              ? new Date(
                                  submission.verified_at,
                                ).toLocaleDateString()
                              : "—"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className="max-w-xs truncate text-sm text-gray-500 dark:text-gray-400 group-hover/row:text-gray-600 dark:group-hover/row:text-gray-300 transition-colors"
                            title={submission.admin_notes || ""}
                          >
                            {submission.admin_notes || "—"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewDetails(submission)}
                              disabled={updatingId === submission.id}
                              className={`p-2 rounded-lg hover:bg-blue-500/10 transition-colors disabled:opacity-50 group/view ${
                                updatingId === submission.id
                                  ? "bg-blue-500/20"
                                  : ""
                              }`}
                              title="View Details"
                            >
                              {updatingId === submission.id ? (
                                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                              ) : (
                                <Eye className="h-4 w-4 text-blue-500 group-hover/view:scale-110 transition-transform" />
                              )}
                            </button>

                            {/* Only show approve/reject buttons for pending submissions */}
                            {submission.status === "pending" && (
                              <>
                                <button
                                  onClick={() =>
                                    handleStatusUpdateClick(submission)
                                  }
                                  disabled={updatingStatus !== ""}
                                  className={`p-2 rounded-lg hover:bg-green-500/10 transition-colors disabled:opacity-50 group/approve ${
                                    updatingStatus === "approved" &&
                                    selectedSubmission?.id === submission.id
                                      ? "bg-green-500/20"
                                      : ""
                                  }`}
                                  title="Approve"
                                >
                                  {updatingStatus === "approved" &&
                                  selectedSubmission?.id === submission.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-green-500" />
                                  ) : (
                                    <CheckCircle className="h-4 w-4 text-green-500 group-hover/approve:scale-110 transition-transform" />
                                  )}
                                </button>
                                <button
                                  onClick={() =>
                                    handleStatusUpdateClick(submission)
                                  }
                                  disabled={updatingStatus !== ""}
                                  className={`p-2 rounded-lg hover:bg-red-500/10 transition-colors disabled:opacity-50 group/reject ${
                                    updatingStatus === "rejected" &&
                                    selectedSubmission?.id === submission.id
                                      ? "bg-red-500/20"
                                      : ""
                                  }`}
                                  title="Reject"
                                >
                                  {updatingStatus === "rejected" &&
                                  selectedSubmission?.id === submission.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-red-500" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500 group-hover/reject:scale-110 transition-transform" />
                                  )}
                                </button>
                              </>
                            )}

                            {/* Show disabled buttons for approved/rejected submissions */}
                            {submission.status !== "pending" && (
                              <div className="flex items-center gap-1">
                                <div className="p-2 opacity-50">
                                  {submission.status === "approved" ? (
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                  )}
                                </div>
                                <span className="text-xs text-gray-500">
                                  {submission.status}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12">
                      <div className="text-center">
                        <div className="relative mx-auto w-20 h-20 mb-4">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-full blur-lg"></div>
                          <div className="relative w-20 h-20 bg-white/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30">
                            <Shield className="h-8 w-8 text-gray-400" />
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          No KYC submissions found
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                          {kycSubmissions.length === 0
                            ? "No KYC submissions have been made yet."
                            : "Try adjusting your filters or search terms"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 border-t border-gray-200/30 dark:border-gray-700/30 gap-4 backdrop-blur-sm">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {Math.min(
                    (pagination.current_page - 1) * pagination.per_page + 1,
                    pagination.total,
                  )}
                </span>{" "}
                to{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {Math.min(
                    pagination.current_page * pagination.per_page,
                    pagination.total,
                  )}
                </span>{" "}
                of{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {pagination.total}
                </span>{" "}
                submissions
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1 || loading}
                  className={`p-2 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed group/prev`}
                  title="Previous page"
                >
                  <ChevronLeft className="h-4 w-4 transition-transform group-hover/prev:-translate-x-0.5" />
                </button>

                {renderPagination()}

                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={
                    pagination.current_page === pagination.last_page || loading
                  }
                  className={`p-2 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed group/next`}
                  title="Next page"
                >
                  <ChevronRight className="h-4 w-4 transition-transform group-hover/next:translate-x-0.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <KycDetailsModal
        submission={selectedSubmission}
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedSubmission(null);
        }}
      />

      <StatusUpdateModal
        submission={selectedSubmission}
        isOpen={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false);
          setSelectedSubmission(null);
        }}
        onConfirm={handleStatusUpdate}
        isLoading={updatingStatus !== ""}
      />
    </div>
  );
};

export default AdminKyc;
